import { ClockIcon, HeartIcon } from "lucide-react";
import { memo } from "react";

import { ArtistLink, ArtistsLinks } from "@/app/components/song/artist-link";
import PlaySongButton from "@/app/components/table/play-button";
import { SongTableActions } from "@/app/components/table/song-actions";
import { TableSongTitle } from "@/app/components/table/song-title";
import { Badge } from "@/app/components/ui/badge";
import { DataTableColumnHeader } from "@/app/components/ui/data-table-column-header";
import { SimpleTooltip } from "@/app/components/ui/simple-tooltip";
import {
  columnProps,
  getLayoutMap,
  getSongColumnLayouts,
} from "@/app/tables/column-layouts";
import i18n from "@/i18n";
import { ColumnDefType } from "@/types/react-table/columnDef";
import { ISong } from "@/types/responses/song";
import { convertSecondsToTime } from "@/utils/convertSecondsToTime";
import dateTime from "@/utils/dateTime";

const MemoSimpleTooltip = memo(SimpleTooltip);
const MemoBadge = memo(Badge);
const MemoPlaySongButton = memo(PlaySongButton);
const MemoTableSongTitle = memo(TableSongTitle);
const MemoSongTableActions = memo(SongTableActions);
const MemoDataTableColumnHeader = memo(
  DataTableColumnHeader,
) as typeof DataTableColumnHeader;

type SongsColumnsOptions = {
  hasHover?: boolean;
};

export function songsColumns({
  hasHover = true,
}: SongsColumnsOptions = {}): ColumnDefType<ISong>[] {
  const layouts = getLayoutMap(getSongColumnLayouts({ hasHover }));

  return [
    {
      id: "index",
      accessorKey: "index",
      ...columnProps(layouts.index),
      header: () => {
        return <div className="w-full text-center">#</div>;
      },
      cell: ({ row }) => {
        const trackNumber = row.index + 1;
        const song = row.original;

        return (
          <MemoPlaySongButton trackNumber={trackNumber} trackId={song.id} />
        );
      },
    },
    {
      id: "trackNumber",
      accessorKey: "track",
      ...columnProps(layouts.trackNumber),
      header: () => {
        return <div className="w-full text-center">#</div>;
      },
      cell: ({ row }) => {
        const song = row.original;
        const trackNumber = song.track;

        return (
          <MemoPlaySongButton trackNumber={trackNumber} trackId={song.id} />
        );
      },
    },
    {
      id: "title",
      accessorKey: "title",
      ...columnProps(layouts.title),
      enableSorting: true,
      sortingFn: "customSortFn",
      header: ({ column, table }) => (
        <MemoDataTableColumnHeader column={column} table={table}>
          {i18n.t("table.columns.title")}
        </MemoDataTableColumnHeader>
      ),
      cell: ({ row, table }) => (
        <MemoTableSongTitle
          song={row.original}
          onPlay={() => table.options.meta?.handlePlaySong?.(row)}
        />
      ),
    },
    {
      id: "artist",
      accessorKey: "artist",
      ...columnProps(layouts.artist),
      enableSorting: true,
      sortingFn: "customSortFn",
      header: ({ column, table }) => (
        <MemoDataTableColumnHeader column={column} table={table}>
          {i18n.t("table.columns.artist")}
        </MemoDataTableColumnHeader>
      ),
      cell: ({ row }) => {
        const { artist, artistId, artists } = row.original;

        if (artists && artists.length > 1) {
          return <ArtistsLinks artists={artists} disableNavigation={true} />;
        }

        if (!artistId) return artist;

        return (
          <ArtistLink artistId={artistId} disableNavigation={true}>
            {artist}
          </ArtistLink>
        );
      },
    },
    {
      id: "album",
      accessorKey: "album",
      ...columnProps(layouts.album),
      enableSorting: true,
      sortingFn: "customSortFn",
      header: ({ column, table }) => (
        <MemoDataTableColumnHeader column={column} table={table}>
          {i18n.t("table.columns.album")}
        </MemoDataTableColumnHeader>
      ),
      cell: ({ row }) => {
        return <span className="truncate">{row.original.album}</span>;
      },
    },
    {
      id: "year",
      accessorKey: "year",
      header: i18n.t("table.columns.year"),
      ...columnProps(layouts.year),
    },
    {
      id: "duration",
      accessorKey: "duration",
      ...columnProps(layouts.duration),
      enableSorting: true,
      sortingFn: "basic",
      header: ({ column, table }) => (
        <MemoSimpleTooltip text={i18n.t("table.columns.duration")}>
          <div>
            <MemoDataTableColumnHeader column={column} table={table}>
              <ClockIcon className="w-4 h-4" />
            </MemoDataTableColumnHeader>
          </div>
        </MemoSimpleTooltip>
      ),
      cell: ({ row }) => {
        const { duration } = row.original;
        const formattedDuration = convertSecondsToTime(duration ?? 0);

        return formattedDuration;
      },
    },
    {
      id: "playCount",
      accessorKey: "playCount",
      ...columnProps(layouts.playCount),
      enableSorting: true,
      sortingFn: "basic",
      sortUndefined: -1,
      header: ({ column, table }) => (
        <MemoDataTableColumnHeader column={column} table={table}>
          {i18n.t("table.columns.plays")}
        </MemoDataTableColumnHeader>
      ),
      cell: ({ row }) => row.original.playCount ?? 0,
    },
    {
      id: "played",
      accessorKey: "played",
      header: i18n.t("table.columns.lastPlayed"),
      ...columnProps(layouts.played),
      cell: ({ row }) => {
        const { played } = row.original;

        if (played) {
          const lastPlayed = dateTime().from(dateTime(played), true);
          return i18n.t("table.lastPlayed", { date: lastPlayed });
        }

        return "";
      },
    },
    {
      id: "bpm",
      accessorKey: "bpm",
      header: i18n.t("table.columns.bpm"),
      ...columnProps(layouts.bpm),
    },
    {
      id: "bitRate",
      accessorKey: "bitRate",
      header: i18n.t("table.columns.bitrate"),
      ...columnProps(layouts.bitRate),
      cell: ({ row }) => {
        return `${row.original.bitRate} kbps`;
      },
    },
    {
      id: "contentType",
      accessorKey: "contentType",
      header: i18n.t("table.columns.quality"),
      ...columnProps(layouts.contentType),
      cell: ({ row }) => {
        const { suffix, bitRate, size } = row.original;
        const tooltipContent = `
          ${i18n.t("table.columns.quality")}: ${suffix}
          ${i18n.t("table.columns.bitrate")}: ${bitRate} kbps
          ${i18n.t("table.columns.size")}: ${
            size / 1024 / 1024 > 1
              ? (size / 1024 / 1024).toFixed(2) + " MB"
              : (size / 1024).toFixed(2) + " KB"
          }
          `;
        return (
          <MemoSimpleTooltip text={tooltipContent}>
            <div>
              <MemoBadge variant="secondary">{suffix.toUpperCase()}</MemoBadge>
            </div>
          </MemoSimpleTooltip>
        );
      },
    },
    {
      id: "select",
      ...columnProps(layouts.select),
      header: () =>
        hasHover ? (
          <MemoSimpleTooltip text={i18n.t("table.columns.favorite")}>
            <HeartIcon className="w-4 h-4 mr-2" />
          </MemoSimpleTooltip>
        ) : null,
      cell: ({ row }) => <MemoSongTableActions row={row} />,
    },
  ];
}
